import { useSyncExternalStore } from 'react';

/**
 * PWA install-prompt state machine.
 *
 * `beforeinstallprompt` fires once, early, near page load — so the listener
 * must exist at module load (side-effect import in main.tsx), not when a
 * component mounts, or the event is missed and the app looks un-installable.
 * We stash the event here and expose the derived state via useSyncExternalStore.
 *
 * Detection order (first match wins): installed → installable → ios-manual →
 * unsupported.
 */
export type InstallState = 'installed' | 'installable' | 'ios-manual' | 'unsupported';

/** The non-standard beforeinstallprompt event, narrowed to what we use. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();

function notify() {
  for (const l of listeners) l();
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const displayMode =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;
  // iOS Safari exposes standalone on navigator, not through display-mode.
  const iosStandalone =
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return displayMode || iosStandalone;
}

function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  const platform = navigator.platform ?? '';
  if (/^(iPhone|iPad|iPod)/.test(platform)) return true;
  // iPadOS 13+ reports MacIntel; a touch-capable "Mac" is really an iPad.
  return platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

function currentState(): InstallState {
  if (installed || isStandalone()) return 'installed';
  if (deferredPrompt) return 'installable';
  // Any iOS browser — install itself still requires Safari's Share sheet,
  // so every ios-manual copy directs the user there.
  if (isIos()) return 'ios-manual';
  return 'unsupported';
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Stop Chrome's own mini-infobar; we drive the prompt from our UI.
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    installed = true;
    notify();
  });
}

/** Fire the stashed browser prompt, wait for the user's choice, drop the stash.
 *  A no-op when nothing is stashed. On accept, the 'installed' state arrives
 *  via the appinstalled event. */
export async function promptInstall(): Promise<void> {
  const evt = deferredPrompt;
  if (!evt) return;
  await evt.prompt();
  await evt.userChoice;
  deferredPrompt = null;
  notify();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Live install state for components. jsdom (no matchMedia) → 'unsupported'. */
export function useInstallPrompt(): InstallState {
  return useSyncExternalStore(subscribe, currentState, () => 'unsupported');
}
